import React, { useState, useEffect } from 'react';
import { Download, Bell, TestTube, Loader2, Code } from 'lucide-react';
import { getPartnerNotificationConfig, updatePartnerNotificationConfig, testPartnerNotification, exportContractOpenAPI } from '../../api';
import Modal from '../Modal';

const ContractSettingsModal = ({
    contract,
    isOpen,
    onClose,
    addToast
}) => {
    // Partner notification state
    const [partnerUrl, setPartnerUrl] = useState('');
    const [partnerEnabled, setPartnerEnabled] = useState(true);
    const [partnerAuthHeader, setPartnerAuthHeader] = useState('');
    const [partnerLoading, setPartnerLoading] = useState(false);
    const [partnerTesting, setPartnerTesting] = useState(false);
    const [partnerTestResult, setPartnerTestResult] = useState(null);
    const [partnerHasChanges, setPartnerHasChanges] = useState(false);
    const [partnerSaving, setPartnerSaving] = useState(false);

    // Load partner config when modal opens
    useEffect(() => {
        if (isOpen && contract?.id) {
            loadPartnerConfig();
        }
    }, [isOpen, contract?.id]);

    const loadPartnerConfig = async () => {
        if (!contract?.id) return;
        setPartnerLoading(true);
        try {
            const data = await getPartnerNotificationConfig(contract.id);
            setPartnerUrl(data.partner_webhook_url || '');
            setPartnerEnabled(data.partner_notifications_enabled !== false);
            setPartnerAuthHeader(data.partner_webhook_headers?.Authorization || '');
            setPartnerHasChanges(false);
            setPartnerTestResult(null);
        } catch (err) {
            console.error('Failed to load partner config:', err);
        } finally {
            setPartnerLoading(false);
        }
    };

    const handleSavePartnerConfig = async () => {
        setPartnerSaving(true);
        try {
            const config = {
                partner_webhook_url: partnerUrl || null,
                partner_notifications_enabled: partnerEnabled,
                partner_webhook_headers: partnerAuthHeader ? { Authorization: partnerAuthHeader } : null,
            };
            await updatePartnerNotificationConfig(contract.id, config);
            setPartnerHasChanges(false);
            addToast('Partner notification settings saved', 'success');
        } catch (err) {
            addToast('Failed to save partner settings', 'error');
        } finally {
            setPartnerSaving(false);
        }
    };

    const handleTestPartner = async () => {
        setPartnerTesting(true);
        setPartnerTestResult(null);
        try {
            if (partnerHasChanges) {
                await handleSavePartnerConfig();
            }
            const result = await testPartnerNotification(contract.id);
            setPartnerTestResult(result);
            addToast(result.success ? 'Test notification sent!' : 'Test failed', result.success ? 'success' : 'error');
        } catch (err) {
            setPartnerTestResult({ success: false, message: err.response?.data?.detail || 'Test failed' });
            addToast('Test failed: ' + (err.response?.data?.detail || err.message), 'error');
        } finally {
            setPartnerTesting(false);
        }
    };

    const handleExportContract = (format) => {
        if (!contract) return;
        const now = new Date();
        const dateStr = now.toISOString().split('T')[0];
        const sanitizedName = contract.name.replace(/[^a-zA-Z0-9-_]/g, '_').toLowerCase();

        let content, filename, mimeType;
        switch (format) {
            case 'json':
                content = JSON.stringify({
                    contractName: contract.name,
                    exportedAt: now.toISOString(),
                    description: contract.description,
                    schema: contract.schema_content,
                    metadata: {
                        id: contract.id,
                        tags: contract.tags || [],
                        createdAt: contract.created_at,
                        updatedAt: contract.updated_at
                    }
                }, null, 2);
                filename = `${sanitizedName}_contract_${dateStr}.json`;
                mimeType = 'application/json';
                break;
            case 'csv':
                const fields = extractFields(contract.schema_content);
                content = convertToCSV(fields);
                filename = `${sanitizedName}_fields_${dateStr}.csv`;
                mimeType = 'text/csv';
                break;
            case 'raw':
            default:
                content = JSON.stringify(contract.schema_content, null, 2);
                filename = `${sanitizedName}_schema_${dateStr}.json`;
                mimeType = 'application/json';
                break;
        }

        const blob = new Blob([content], { type: mimeType });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => window.URL.revokeObjectURL(url), 150);
        addToast(`Exported as ${format.toUpperCase()}`, 'success');
    };

    const extractFields = (schema, prefix = '') => {
        const fields = [];
        if (typeof schema !== 'object' || schema === null) return fields;
        for (const [key, value] of Object.entries(schema)) {
            const fieldPath = prefix ? `${prefix}.${key}` : key;
            if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                fields.push(...extractFields(value, fieldPath));
            } else {
                fields.push({
                    field: fieldPath,
                    type: Array.isArray(value) ? 'array' : typeof value,
                    example: Array.isArray(value) && value.length > 0 ? JSON.stringify(value[0]).slice(0, 50) : JSON.stringify(value).slice(0, 50)
                });
            }
        }
        return fields;
    };

    const convertToCSV = (fields) => {
        if (fields.length === 0) return 'field,type,example\n';
        const headers = Object.keys(fields[0]).join(',');
        const rows = fields.map(f =>
            Object.values(f).map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')
        ).join('\n');
        return `${headers}\n${rows}`;
    };

    if (!isOpen || !contract) return null;

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={`Settings: ${contract.name}`} maxWidth="max-w-2xl">
            <div className="space-y-6">
                {/* Contract Info */}
                <div className="bg-[#1e1e1e] p-4 rounded-lg border border-[#3c3c3c]">
                    <h4 className="text-sm font-medium text-white mb-2">
                        Contract Details
                    </h4>
                    <div className="text-sm text-[#808080] space-y-1">
                        <p>ID: {contract.id}</p>
                        <p>Name: {contract.name}</p>
                        {contract.description && (
                            <p>Description: {contract.description}</p>
                        )}
                    </div>
                </div>

                {/* Partner Notification Section */}
                <div className="border-t border-[#3c3c3c] pt-4">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <Bell size={16} className="text-amber-400" />
                            <h4 className="text-sm font-medium text-white">
                                Partner Drift Notifications
                            </h4>
                        </div>
                        {partnerUrl && (
                            <button
                                onClick={() => {
                                    setPartnerEnabled(!partnerEnabled);
                                    setPartnerHasChanges(true);
                                }}
                                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${partnerEnabled ? 'bg-green-600' : 'bg-[#4c4c4c]'
                                    }`}
                            >
                                <span
                                    className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${partnerEnabled ? 'translate-x-4' : 'translate-x-1'
                                        }`}
                                />
                            </button>
                        )}
                    </div>

                    <p className="text-xs text-[#808080] mb-3">
                        Alert trading partners via HTTP webhook when drift is detected against this contract.
                    </p>

                    {partnerLoading ? (
                        <div className="text-center py-4 text-[#808080] text-sm">Loading...</div>
                    ) : (
                        <div className="space-y-3">
                            <div>
                                <label className="block text-xs font-medium text-[#cccccc] mb-1">
                                    Webhook URL
                                </label>
                                <input
                                    type="url"
                                    value={partnerUrl}
                                    onChange={(e) => {
                                        setPartnerUrl(e.target.value);
                                        setPartnerHasChanges(true);
                                        setPartnerTestResult(null);
                                    }}
                                    className="w-full px-3 py-2 bg-[#1e1e1e] border border-[#3c3c3c] rounded-lg text-white text-sm placeholder-[#606060] focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                                    placeholder="https://partner-api.example.com/drift-webhook"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-[#cccccc] mb-1">
                                    Authorization Header <span className="text-[#606060]">(optional)</span>
                                </label>
                                <input
                                    type="text"
                                    value={partnerAuthHeader}
                                    onChange={(e) => {
                                        setPartnerAuthHeader(e.target.value);
                                        setPartnerHasChanges(true);
                                    }}
                                    className="w-full px-3 py-2 bg-[#1e1e1e] border border-[#3c3c3c] rounded-lg text-white text-sm placeholder-[#606060] focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                                    placeholder="Bearer partner_token_xxx"
                                />
                            </div>

                            {/* Test Result */}
                            {partnerTestResult && (
                                <div className={`p-2.5 rounded-lg text-sm ${partnerTestResult.success
                                        ? 'bg-green-900/20 text-green-400 border border-green-800/30'
                                        : 'bg-red-900/20 text-red-400 border border-red-800/30'
                                    }`}>
                                    {partnerTestResult.success ? '✓' : '✗'} {partnerTestResult.message}
                                </div>
                            )}

                            <div className="flex gap-2">
                                <button
                                    onClick={handleSavePartnerConfig}
                                    disabled={!partnerHasChanges || partnerSaving}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                >
                                    {partnerSaving ? 'Saving...' : 'Save'}
                                </button>
                                <button
                                    onClick={handleTestPartner}
                                    disabled={!partnerUrl || partnerTesting}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[#cccccc] bg-[#3c3c3c] rounded-lg hover:bg-[#4c4c4c] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                >
                                    {partnerTesting ? (
                                        <><Loader2 size={14} className="animate-spin" /> Testing...</>
                                    ) : (
                                        <><TestTube size={14} /> Send Test</>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Export Contract Section */}
                <div className="border-t border-[#3c3c3c] pt-4">
                    <h4 className="text-sm font-medium text-white mb-3">
                        Export Contract
                    </h4>
                    <div className="flex flex-wrap gap-2">
                        <button
                            onClick={() => handleExportContract('json')}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[#cccccc] bg-[#3c3c3c] border border-[#4c4c4c] rounded-lg hover:bg-[#4c4c4c] transition-colors"
                        >
                            <Download size={14} />
                            JSON
                        </button>
                        <button
                            onClick={() => handleExportContract('csv')}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[#cccccc] bg-[#3c3c3c] border border-[#4c4c4c] rounded-lg hover:bg-[#4c4c4c] transition-colors"
                        >
                            <Download size={14} />
                            CSV (Fields)
                        </button>
                        <button
                            onClick={() => handleExportContract('raw')}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[#cccccc] bg-[#3c3c3c] border border-[#4c4c4c] rounded-lg hover:bg-[#4c4c4c] transition-colors"
                        >
                            <Download size={14} />
                            Raw Schema
                        </button>
                        <button
                            onClick={async () => {
                                try {
                                    await exportContractOpenAPI(contract.id, 'yaml');
                                    addToast('OpenAPI YAML exported', 'success');
                                } catch (e) {
                                    addToast('Failed to export OpenAPI', 'error');
                                }
                            }}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-amber-400 bg-amber-900/20 border border-amber-800/30 rounded-lg hover:bg-amber-900/30 transition-colors"
                        >
                            <Code size={14} />
                            OpenAPI
                        </button>
                    </div>
                </div>

                {/* Close Button */}
                <div className="flex justify-end pt-4 border-t border-[#3c3c3c]">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-[#cccccc] bg-[#3c3c3c] rounded-lg hover:bg-[#4c4c4c]"
                    >
                        Close
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default ContractSettingsModal;
